type LocalizedStringMap = Record<string, string>;

function hasLocalizedStringValue(
  localized: LocalizedStringMap | undefined | null
): boolean {
  if (!localized || typeof localized !== "object") {
    return false;
  }
  return Object.values(localized).some(
    (value) => typeof value === "string" && value.trim().length > 0
  );
}

export type ValidationResult =
  | { ok: true }
  | { ok: false; reason: string };

export const ok: ValidationResult = { ok: true };

export function fail(reason: string): ValidationResult {
  return { ok: false, reason };
}

export function rejectInternalId(item: Record<string, unknown>): ValidationResult {
  if ("id" in item) {
    return fail('"id" is not allowed in import JSON; use "code" instead');
  }
  return ok;
}

export function optionalImportDocumentId(value: unknown): ValidationResult {
  if (value === undefined) {
    return ok;
  }
  if (typeof value !== "string" || value.trim().length === 0) {
    return fail("id must be a non-empty string when provided");
  }
  if (value.includes("/")) {
    return fail("id must not contain '/'");
  }
  return ok;
}

export function optionalString(
  value: unknown,
  field: string
): ValidationResult {
  if (value === undefined || value === null || value === "") {
    return ok;
  }
  if (typeof value !== "string") {
    return fail(`${field} must be a string`);
  }
  return ok;
}

export function requireNonEmptyString(
  value: unknown,
  field: string
): ValidationResult {
  if (typeof value !== "string" || value.trim().length === 0) {
    return fail(`${field} is required`);
  }
  return ok;
}

export function requireExactLength(
  value: unknown,
  field: string,
  length: number
): ValidationResult {
  const base = requireNonEmptyString(value, field);
  if (!base.ok) {
    return base;
  }
  if ((value as string).length !== length) {
    return fail(`${field} must be exactly ${length} characters`);
  }
  return ok;
}

export function requireLocalizedString(
  value: unknown,
  field: string
): ValidationResult {
  if (!hasLocalizedStringValue(value as LocalizedStringMap | undefined)) {
    return fail(`${field} must have at least one non-empty translation`);
  }
  return ok;
}

export function requireEnum(
  value: unknown,
  field: string,
  allowed: readonly string[]
): ValidationResult {
  if (typeof value !== "string" || !allowed.includes(value)) {
    return fail(`${field} must be one of: ${allowed.join(", ")}`);
  }
  return ok;
}

export function requireNumberInRange(
  value: unknown,
  field: string,
  min: number,
  max: number
): ValidationResult {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return fail(`${field} must be a number`);
  }
  if (value < min || value > max) {
    return fail(`${field} must be between ${min} and ${max}`);
  }
  return ok;
}

export function mergeResults(...results: ValidationResult[]): ValidationResult {
  for (const result of results) {
    if (!result.ok) {
      return result;
    }
  }
  return ok;
}
